/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Video Pilot
 *
 * Object-based pattern for video autopilot behaviors.
 * Allows downstream packages to override specific behavior handlers.
 *
 * Handles:
 * - Enter behaviors (init, autoplay, resume)
 * - Leave behaviors (autopause, stop)
 */
export const videoPilot = {
  /**
   * Execute enter behaviors when video becomes visible
   *
   * @param {Object} params
   * @param {Object} params.player - Video player instance
   * @param {boolean} params.initialized - Whether player is initialized
   * @param {Function} params.init - Init function for lazy loading
   * @param {string[]} params.behaviors - List of behavior names
   * @returns {Promise<void>}
   */
  async enter({player, initialized, init, behaviors = ['init', 'autoplay']}) {
    for (const action of behaviors) {
      try {
        if (action === 'init' && !player) {
          await init();
        } else if (action === 'autoplay' && player && initialized) {
          await player.play();
        } else if (action === 'resume' && player && initialized) {
          const currentTime = await player.call('getCurrentTime').catch(() => 0);

          if (currentTime > 0) {
            await player.play();
          }
        }
      } catch (error) {
        if (error.name !== 'NotAllowedError') {
          console.log(`Autopilot action "${action}" failed:`, error);
        }
      }
    }
  },

  /**
   * Execute leave behaviors when video becomes hidden
   *
   * @param {Object} params
   * @param {Object} params.player - Video player instance
   * @param {boolean} params.initialized - Whether player is initialized
   * @param {string[]} params.behaviors - List of behavior names
   * @returns {Promise<void>}
   */
  async leave({player, initialized, behaviors = ['autopause']}) {
    for (const action of behaviors) {
      try {
        if (action === 'autopause' && player && initialized) {
          await player.pause();
        } else if (action === 'stop' && player && initialized) {
          await player.pause();
          await player.call('setCurrentTime', 0);
        }
      } catch (err) {
        console.warn(`Autopilot action "${action}" failed:`, err);
      }
    }
  },
};
